// MecTURING Accidental Care Plan — Terms & Conditions copy.
// Source of truth: "Mecturing Accidental Plan TnCs page" design export. Keep wording exact.

import {
  callout,
  clause,
  contactSection,
  dash,
  note,
  numbered,
  slaSection,
  step,
  type TncHero,
  type TncSection,
} from "@/components/tnc/tncTypes";

export const mecturingTncHero: TncHero = {
  breadcrumb: "MecTURING Accidental Care Plan",
  current: "Terms & Conditions",
  title: "Terms and Conditions",
  lines: ["Accidental Damage Protection Plan", "For products purchased on Mecturing."],
};

export const mecturingTncFooterNote =
  "MecTURING Accidental Care Plan is administered by EmbedMike India Private Limited (Protega) on behalf of MecTURING. It is a service plan, not an insurance policy, and the Terms and Conditions govern in full.";

export const mecturingTncSections: TncSection[] = [
  {
    kind: "clauses",
    id: "plan-introduction",
    title: "Plan introduction",
    blocks: [
      clause(
        "The Mecturing Accidental Damage Protection (ADP) Plan (“Plan”) provides coverage for sudden, accidental damage to the Covered Product during normal household use in India, for the Policy Term, subject to these Terms and Conditions (“Terms”)."
      ),
      clause(
        "The Plan is optional and paid. It is offered and administered by EmbedMike India Private Limited (“Protega”), acting on behalf of Mecturing. Protega may appoint Authorised Service Partners to perform any part of the Plan and remains responsible to the Customer for its obligations under the Plan."
      ),
      clause("Coverage includes:"),
      dash("Drops or impacts"),
      dash("Collisions or sudden external force"),
      dash("Single-event liquid spills"),
      dash("Pet damage"),
      clause(
        "The Plan does not replace, extend or alter the manufacturer’s warranty and is not an insurance policy. Defects covered by the manufacturer’s warranty must be claimed under that warranty."
      ),
      clause(
        "Coverage is limited to India, is tied to the original purchased unit identified in the Certificate of Protection, and is serviced only through Protega or its Authorised Service Partners."
      ),
      clause(
        "A fixed deductible applies per approved claim (RVC ₹699, Mixer ₹399, or as stated in your Certificate of Protection) and must be paid before claim processing. The deductible is non-refundable once the RMA is issued.",
        "Deductible:"
      ),
      clause(
        "Your Covered Product is protected against ADH during the Policy Term. The use of this coverage requires an explanation of where and when the accident occurred as well as a detailed description of the actual event. Failure to provide this information, or providing information that is inaccurate or inconsistent with the damage observed, will result in claim denial.",
        "ADH (Accidental Damage from Handling):"
      ),
      clause(
        "The Certificate of Protection, these Terms, the invoice for the Product and the Plan, and the payment confirmation together form the Plan. If the Certificate of Protection and these Terms differ, these Terms prevail.",
        "Documents forming the Plan:"
      ),
      clause(
        "Coverage is limited to accidental damage events that are external, visible and verifiable, and that directly cause functional impairment, operational failure or structural damage to the Covered Product. Damage that cannot reasonably be attributed to a defined accidental event is not covered."
      ),
      clause(
        "Coverage is conditional on the Product being used, handled and stored in accordance with the manufacturer’s specifications, user manual, safety instructions and intended use. Any deviation from these conditions that contributes to the damage may result in denial of the claim."
      ),
      clause(
        "Although cover starts when the order is placed, the Plan covers only accidental damage that occurs after the Product has been delivered to the Customer, while the Product is being used for its intended purpose in normal personal or household use. Any damage, loss or defect arising before or during shipping and delivery is not covered and is handled under the Merchant’s return and replacement policy.",
        "When damage is covered:"
      ),
      clause(
        "Where a claim is resolved by replacement or Store Credit, depreciation based on the age of the Product from the invoice date applies instead of the deductible, as set out below. Store Credit is issued for the Invoice Value less depreciation. For a replacement, the Customer pays the depreciation amount before the replacement is dispatched, or may choose Store Credit instead. Any deductible already paid is adjusted against the depreciation amount or added to the Store Credit. Claims resolved by repair are not subject to depreciation.",
        "Depreciation on replacement or store credit:"
      ),
      {
        kind: "table",
        head: ["Age of product at claim", "Depreciation"],
        rows: [
          ["Up to 6 months", "10%"],
          ["6 to 12 months", "20%"],
          ["12 to 18 months", "30%"],
          ["18 to 24 months", "40%"],
        ],
      },
    ],
  },
  {
    kind: "clauses",
    id: "customer-responsibilities",
    title: "Customer responsibilities",
    blocks: [
      clause(
        "Retain your purchase invoice, serial labels, and ADP certificate for the full Policy Term."
      ),
      clause("Report incidents promptly within 72 hours of the accidental event."),
      clause(
        "Avoid unauthorized repairs, modifications, or tampering, and do not hand the product to any service centre before Protega approves the claim."
      ),
      clause("Maintain products according to the manufacturer’s care instructions."),
      clause(
        "Stop using the product after an incident where continued use may worsen the damage, and take reasonable steps to prevent further damage."
      ),
      clause(
        "Provide true, complete and accurate information and documents at all times."
      ),
      clause(
        "Cooperate during inspection, packing, and pickup, and hand over the product complete with all parts and accessories supplied with it."
      ),
      clause(
        "Ensure personal data is removed if requested during claims (back up any profiles/settings if applicable)."
      ),
      clause(
        "Keep your registered email address, mobile number and WhatsApp number up to date."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "how-to-file-a-claim",
    title: "How to file a claim",
    blocks: [
      step("Contact methods"),
      dash("www.protega.ai/customer-login", "Web portal:"),
      dash("+91 93240 95283", "WhatsApp:"),
      note("Claims are accepted only through these two channels.", true),
      step("Required documents"),
      dash("ADP certificate and purchase invoice"),
      dash(
        "Original, unedited photos or videos showing damage clearly (front, back/base, close-ups ×2, control panels/ports and the serial label)"
      ),
      dash("Detailed description of the incident (when, where and how it occurred)"),
      note(
        "Protega may request additional information, documents or a live video inspection before approving a claim. A claim will not be processed until the requested information is provided."
      ),
      step("Claim steps"),
      numbered(
        "Report within 72 hours of the accidental event with all required details.",
        "Protega reviews the claim and decides Repair/Replace.",
        "Deductible payment: upon approval, a secure payment link is sent. RMA issued only after deductible payment.",
        "Packaging & pickup arranged; customer cooperation required.",
        "Repair/replacement completed; QC performed; replacement dispatched if applicable.",
        "Closure: customer notified with service report or new serial details."
      ),
      clause(
        "The product must not be repaired, opened or handed to any service centre before Protega approves the claim in writing. Costs incurred before approval will not be reimbursed, and such a claim may be rejected.",
        "Approval before repair:"
      ),
      clause(
        "The deductible must be paid within 7 days of the payment link being sent. No pickup, inspection or repair takes place before payment. If the deductible is not paid within this time, the claim is treated as withdrawn and closed, and cannot be re-opened. No new claim will be accepted for the same incident. Where Protega has already incurred inspection or logistics costs, the Customer bears these costs before the product is returned.",
        "Deductible deadline:"
      ),
      clause(
        "Claims reported after 72 hours of the accidental event may be rejected.",
        "Late reporting:"
      ),
      note("Claim limit: One valid claim per product per Policy Term.", true),
    ],
  },
  {
    kind: "clauses",
    id: "definitions",
    title: "Definitions",
    blocks: [
      clause(
        "Person purchasing a Mecturing product with Protection Plan, whose name appears on the invoice.",
        "Customer:"
      ),
      clause("Mecturing.", "Merchant:"),
      clause("EmbedMike India Private Limited, Plan administrator.", "Protega:"),
      clause(
        "Eligible Mecturing item covered under this Plan and identified in the Certificate of Protection.",
        "Product:"
      ),
      clause(
        "Sudden, unintended and unforeseen event from an external cause, occurring while the Product is used for its intended purpose in a normal household setting and in accordance with the manufacturer’s instructions, causing physical damage that impairs normal use (drops, spills, pet damage, impacts).",
        "Accidental Damage (AD):"
      ),
      clause(
        "Any act or omission showing reckless disregard for reasonable care, product safety or the manufacturer’s guidelines.",
        "Gross Negligence:"
      ),
      clause(
        "Use of the Product by the Customer for personal, non-business purposes only.",
        "Personal Use:"
      ),
      clause(
        "Use of the Product for paid work, rental, resale, or by or for a business, institution or office.",
        "Commercial Use:"
      ),
      clause(
        "12 or 24 months from the date the order for the Product and Plan is placed.",
        "Policy Term:"
      ),
      clause(
        "The price paid for the Product as shown on the purchase invoice, after any discounts and including applicable taxes, but excluding shipping or delivery charges, convenience or COD fees, installation charges, EMI interest or fees, and the Plan fee.",
        "Invoice Value:"
      ),
      clause(
        "The Invoice Value of the product. This is the maximum liability under the Plan, across all claims.",
        "Coverage Limit:"
      ),
      clause(
        "The reduction applied to the value of a replacement or Store Credit based on the age of the Product, as set out in clause 1.12.",
        "Depreciation:"
      ),
      clause(
        "One valid claim per product, up to the Coverage Limit.",
        "Claim Limit:"
      ),
      clause(
        "Return Merchandise Authorization issued after claim approval and deductible payment.",
        "RMA:"
      ),
      clause(
        "Where the repair cost exceeds the Invoice Value, or the Product cannot be repaired.",
        "Beyond Economic Repair (BER):"
      ),
      clause(
        "A non-cash voucher or credit usable only on Mecturing, issued under clause 8.",
        "Store Credit:"
      ),
      clause(
        "A repair, inspection or logistics partner appointed by Protega.",
        "Authorised Service Partner:"
      ),
      clause(
        "A pincode in India where Protega or its Authorised Service Partners provide pickup or service.",
        "Serviceable Location:"
      ),
    ],
  },
  {
    kind: "clauses",
    id: "eligibility",
    title: "Eligibility",
    blocks: [
      clause(
        "Coverage applies only to new, genuine Mecturing products eligible for the Plan at the time of purchase, bought from authorized channels in India."
      ),
      clause(
        "Refurbished, open-box, second-hand and returned-and-resold units are not eligible."
      ),
      clause(
        "The Plan must be purchased together with the product through the Merchant’s eligible sales channel where the Plan is offered."
      ),
      clause(
        "The product is used for Personal Use only. Commercial Use is excluded."
      ),
      clause(
        "Coverage begins when the order for the Product and Plan is placed and lasts for the chosen Policy Term."
      ),
      clause(
        "The Customer is responsible for purchasing the Plan for the correct Product. Protega may reject a claim if the model or serial number of the Product claimed does not match the Plan."
      ),
      clause(
        "If a Plan is issued on a product that is not eligible, the Plan is void and the Plan fee is refunded in full."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "what-is-covered",
    title: "What is covered",
    blocks: [
      note(
        "Every item below is covered only where it results from a sudden, accidental external event.",
        true
      ),
      clause("Accidental drops, falls or impacts causing physical damage"),
      clause("Damage caused by a sudden, accidental external force or collision"),
      clause(
        "Accidental liquid spills or liquid ingress from a single, unintended event"
      ),
      clause("Accidental pet damage (scratching, chewing, tearing)"),
      clause("Accidental tearing, punctures or crushing"),
      clause(
        "Non-consumable accessories sold with the product, when damaged in the same covered accident"
      ),
      clause("Repair or replacement at Protega/Mecturing discretion"),
      callout(
        "Note",
        "The deductible applies to claims resolved by repair. Claims resolved by replacement or store credit are subject to depreciation instead (clause 1.12)."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "what-is-not-covered",
    title: "What is not covered",
    blocks: [
      clause(
        "Manufacturer defects or faults already covered under the standard manufacturer warranty."
      ),
      clause(
        "Wear & tear, fading, peeling, loosening, or gradual deterioration of performance over time."
      ),
      clause(
        "Cosmetic scratches, dents, or marks that do not affect the product’s functionality."
      ),
      clause(
        "Pre-existing damage or any issue that occurred before the Plan start date."
      ),
      clause(
        "Intentional damage, misuse, or negligence, including reckless or abusive handling."
      ),
      clause(
        "Use outside domestic or personal settings (e.g., commercial, industrial, or rental use)."
      ),
      clause(
        "Consumables (brushes, filters, mop cloths, batteries, bulbs, etc.) unless damaged in a covered accident."
      ),
      clause(
        "Damage from environmental or natural causes such as fire, flood, earthquake, rain, wind, or pest infestation."
      ),
      clause(
        "Power or electrical surges, voltage fluctuations, or short circuits not caused by an external accident."
      ),
      clause(
        "Theft, burglary, misplacement, or loss of the product after delivery."
      ),
      clause(
        "Unauthorized repairs, modifications, or non-genuine parts leading to damage or malfunction."
      ),
      clause(
        "Improper installation, usage, or handling that does not follow the manufacturer’s care and safety instructions."
      ),
      clause(
        "Damage from fire, burns, or smoke beyond what qualifies as accidental damage under ADH coverage."
      ),
      clause(
        "Improper cleaning or chemical exposure, including damage from unsuitable detergents or solvents."
      ),
      clause(
        "Damage arising from failure to perform regular maintenance or cleaning where required."
      ),
      clause(
        "Gradual loss of mixer motor performance, or similar functional deterioration due to age or usage."
      ),
      clause(
        "Noises, squeaks, or performance variations that do not impact product usability."
      ),
      clause(
        "External causes or third-party actions, including accidents during transit after delivery."
      ),
      clause(
        "Software, data, or connectivity issues (for smart or app-enabled devices)."
      ),
      clause(
        "Consequential or incidental losses, such as downtime, loss of use, or replacement delays."
      ),
      clause(
        "Failure or claim raised after the Policy Term or without valid proof of accident and deductible payment."
      ),
      clause(
        "Products whose serial number or label has been altered, defaced or removed."
      ),
      clause(
        "Damage, loss or defects arising before or during shipping and delivery, including transit damage discovered on unboxing. This is handled under Mecturing’s return and replacement policy."
      ),
      clause(
        "Costs payable under any other warranty, insurance policy or protection plan."
      ),
      clause("Recalls and modifications required by the manufacturer."),
      clause(
        "damage from picking up liquids or debris beyond the manufacturer’s specification, or from use with cliff sensors or other safety features disabled or obstructed.",
        "RVCs:"
      ),
      clause(
        "motor, jar or blade damage from running dry, overloading beyond rated capacity or duration, or using non-original jars or blades.",
        "Mixers:"
      ),
      clause(
        "Further damage caused by continued use of the product after the accidental event."
      ),
      clause(
        "Any claim involving fraud, misrepresentation or concealment of facts."
      ),
      callout(
        "Note",
        "In case of an Accidental Damage from Handling (ADH) event, customers must provide a clear description of where and when the incident occurred, with supporting photos/videos. Failure to do so will result in claim denial.",
        "A deductible (as mentioned in the Certificate of Protection) is payable on each approved claim before it is processed. Where the claim is resolved by replacement or store credit, depreciation applies instead and the deductible paid is adjusted against it."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "service-procedures",
    title: "Service procedures & limit of liability",
    blocks: [
      clause(
        "Claims evaluated thoroughly to verify accidental damage. Protega’s decision on whether a claim is covered, and on the mode of resolution, is made on the basis of inspection and the information provided."
      ),
      clause(
        "Default per claim (RVC ₹699, Mixer ₹399, or as stated in your Certificate of Protection) and must be paid before RMA issuance.",
        "Deductible:"
      ),
      clause(
        "Repairs/replacements performed by authorized providers using new or refurbished genuine parts."
      ),
      clause("Voucher/credit issued if repair/replacement isn’t feasible."),
      clause(
        "Maximum liability = Invoice Value. This is Protega’s total aggregate liability across all claims under the Plan."
      ),
      clause("Plan ends once claim is resolved."),
      clause(
        "Protega may request submission of damaged product/parts for inspection."
      ),
      clause(
        "Subrogation rights retained to recover costs from responsible third parties."
      ),
      clause(
        "No cash settlement is made under the Plan. Claims are resolved only by repair, replacement or Store Credit."
      ),
      clause(
        "Replacement and Store Credit are subject to depreciation under clause 1.12, which applies instead of the deductible."
      ),
      clause(
        "Replacement units may be new, refurbished or functionally equivalent, of the same model or, if unavailable, a model of equal or comparable specification, which may differ in colour or design."
      ),
      clause(
        "Store Credit is valid for 6 months from the date of issue, usable only on Mecturing, not transferable and not redeemable for cash."
      ),
      clause(
        "Replaced parts, and any product replaced or settled by Store Credit, become the property of Protega."
      ),
      clause(
        "Where the product is handed over incomplete, the value of missing parts or accessories is deducted from the resolution."
      ),
      clause(
        "If inspection finds no fault, or finds that the damage is not covered, the claim is rejected and the Customer bears the inspection and logistics costs before the product is returned."
      ),
      clause(
        "If the Customer does not accept delivery or respond within 7 days of Protega’s written notice after repair or rejection, Protega may charge reasonable storage costs from that date, and any further delivery attempts will be charged to the Customer. Protega will send a final written notice before disposal, and if the product is still not collected within 10 days of the first notice, Protega may dispose of or recycle it."
      ),
      clause(
        "Where Protega schedules a pickup, doorstep repair visit, or delivery of a repaired or replacement product, the Customer must be available at the time discussed with the Customer before the visit is scheduled, or reschedule at least 24 hours in advance. If the Customer is unavailable, refuses to accept, or does not provide access, the cost of each further attempt is borne by the Customer and must be paid before the next attempt is scheduled. After two missed attempts, Protega may close the claim as withdrawn or, where the product has already been repaired or replaced, apply the storage and disposal terms above.",
        "Missed appointments:"
      ),
      clause(
        "Protega may reset the device and update its firmware or app during repair, and is not liable for loss of data, profiles or settings."
      ),
      clause(
        "If spare parts are no longer available, Protega may resolve the claim by replacement or Store Credit."
      ),
      clause(
        "Protega will make reasonable efforts to meet the timelines in clause 13, but is not liable for delays caused by unavailability of spare parts, logistics disruptions or events beyond its reasonable control."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "cancellation",
    title: "Cancellation",
    blocks: [
      clause(
        "Cancel within 7 days of purchase for a full refund, if no claim has been filed.",
        "Free-look:"
      ),
      clause(
        "If the product is returned or the order is cancelled, the Plan is cancelled automatically and the Plan fee refunded in full, provided no claim has been raised."
      ),
      clause(
        "After free-look or post-claim, plan is non-cancellable and non-refundable."
      ),
      clause(
        "Protega may cancel for fraud, misrepresentation, or non-payment. On cancellation for fraud or misrepresentation, the Plan fee is forfeited and Protega may recover all costs incurred on any claim."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "conditions",
    title: "Conditions",
    blocks: [
      clause(
        "The Plan is non-transferable and applies only to the original purchaser and the covered product."
      ),
      clause("Renewal not available; new plan must be purchased after expiry."),
      clause(
        "Valid within India only. Pickup and on-site service are available only at Serviceable Locations; elsewhere, Protega may arrange courier pickup or ask the Customer to send the product."
      ),
      clause(
        "Protega reserves rights to recover costs from third parties responsible for damage."
      ),
      clause(
        "All notices and communication are sent to the Customer’s registered email address, mobile number or WhatsApp number and are treated as received when sent."
      ),
      clause(
        "Protega may appoint Authorised Service Partners to perform any part of the Plan."
      ),
      clause(
        "Protega is not liable for any failure or delay caused by events beyond its reasonable control, including natural disasters, epidemics, government action, strikes or disruption to logistics."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "legal-disclosures",
    title: "Legal disclosures",
    blocks: [
      clause(
        "Disputes resolved via arbitration under the Arbitration & Conciliation Act, 1996, by a sole arbitrator appointed by mutual agreement or as provided under that Act. The seat and venue of arbitration is Mumbai and the language is English."
      ),
      clause(
        "Nothing in these Terms limits the Customer’s rights or remedies under the Consumer Protection Act, 2019."
      ),
      clause(
        "These Terms are governed by the laws of India. Subject to arbitration, courts at Mumbai have exclusive jurisdiction."
      ),
      clause("Plan and purchase confirmation constitute the entire agreement."),
      clause("Severability ensures invalid clauses do not affect other terms."),
      clause(
        "Customer data processed as per Protega Privacy Policy. Personal data, claim photos and videos are shared with Mecturing and Authorised Service Partners only to administer the Plan, verify claims and prevent fraud. Calls and WhatsApp conversations may be recorded for quality and verification."
      ),
      clause(
        "Protega and Mecturing not liable for indirect, incidental, or consequential losses."
      ),
      clause(
        "Where Protega relies on an exclusion to reject a claim, the Customer must show that the claim is covered."
      ),
      clause(
        "The Customer indemnifies Protega against loss caused by the Customer’s fraud, misrepresentation or breach of these Terms."
      ),
      clause(
        "Protega may amend policies as needed, following regulatory requirements. Amendments will not reduce cover on Plans already issued, except where required by law."
      ),
      clause(
        "A failure or delay by Protega to enforce any term is not a waiver of that term."
      ),
    ],
  },
  {
    kind: "faq",
    id: "faq",
    title: "FAQ",
    items: [
      {
        q: "Is there a deductible for claims?",
        a: "Yes, a deductible applies per approved claim: RVC ₹699, Mixer ₹399, or as stated in your Certificate of Protection. This must be paid via secure link within 7 days, before RMA is issued.",
      },
      {
        q: "Is this the manufacturer’s warranty?",
        a: "No; the manufacturer’s warranty covers defects. ADP covers accidental damage such as drops, impacts, spills or pet damage.",
      },
      {
        q: "How long is coverage?",
        a: "Cover starts as soon as you place the order and lasts for 12 or 24 months, depending on the plan selected. It covers only accidental damage that happens after delivery, during normal intended use. Damage in transit is not covered.",
      },
      {
        q: "How many claims can I make?",
        a: "Only one valid claim per product per Policy Term.",
      },
      {
        q: "What documents are needed to file a claim?",
        a: "You need the purchase invoice, ADP certificate, and photos/videos showing the damage.",
      },
      {
        q: "What happens if repair is not possible?",
        a: "Protega/Mecturing may provide a new, refurbished or comparable replacement, or store credit up to the Invoice Value.",
      },
      {
        q: "Will a replacement be new?",
        a: "It may be new or refurbished, at Protega’s discretion.",
      },
      {
        q: "Can I cancel the plan?",
        a: "Yes, within the free-look period. After that, the plan is non-refundable.",
      },
      {
        q: "What if I return my product?",
        a: "The Plan is cancelled automatically and the Plan fee is refunded, provided no claim has been raised.",
      },
      {
        q: "Are accessories covered?",
        a: "Yes, non-consumable accessories sold with the product are covered. Consumables like brushes, filters, or pads are not covered unless damaged during a covered event.",
      },
      {
        q: "Do I need to register separately?",
        a: "No. The plan is automatically registered when you purchase it with the product.",
      },
      {
        q: "How long does a claim take?",
        a: "Claims are acknowledged within 24 working hours and initially assessed within 24–48 working hours. Standard repairs are typically completed within 5–7 working days; replacements depend on product availability.",
      },
      {
        q: "Are pet or liquid accidents covered?",
        a: "Yes, if they occur accidentally during normal domestic use.",
      },
      {
        q: "Is depreciation applied on a replacement?",
        a: "Yes. Replacement or store credit is reduced by depreciation based on the product’s age, instead of the deductible: 10% up to 6 months, 20% for 6–12 months, 30% for 12–18 months and 40% for 18–24 months. Repairs are not depreciated.",
      },
      {
        q: "Can I get cash instead of a repair?",
        a: "No. Claims are resolved only by repair, replacement or store credit.",
      },
    ],
  },
  slaSection,
  {
    kind: "text",
    id: "grievance-redressal",
    title: "Grievance redressal",
    text: "If you are not satisfied with a claim decision or with our service, write to support@protega.ai with your Policy ID. Every complaint receives a ticket number, is acknowledged within 48 hours and is resolved within 30 days.",
  },
  contactSection,
];
